import { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type DetailSectionProps = {
  id?: string
  title: string
  description?: ReactNode
  actions?: ReactNode
  tone?: 'default' | 'destructive'
  className?: string
  bodyClassName?: string
  /** a section without children is just its header, e.g. a title with an action */
  children?: ReactNode
}

/** a titled card; the building block of the organization tabs */
export const DetailSection = ({
  id,
  title,
  description,
  actions,
  tone = 'default',
  className,
  bodyClassName,
  children
}: DetailSectionProps) => (
  <section
    aria-label={title}
    className={cn(
      'scroll-mt-4 rounded-lg border bg-card text-card-foreground',
      tone === 'destructive' && 'border-destructive/60',
      className
    )}
    id={id}
  >
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 px-5 py-4',
        children && 'border-b'
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <h2
          className={cn(
            'text-base font-semibold',
            tone === 'destructive' && 'text-red-600 dark:text-red-400'
          )}
        >
          {title}
        </h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {actions}
    </div>
    {children && <div className={cn('p-5', bodyClassName)}>{children}</div>}
  </section>
)

export type DefinitionItem = {
  label: string
  value?: ReactNode
  mono?: boolean
}

const isEmpty = (value: ReactNode) =>
  value === undefined || value === null || value === ''

/** read-only facts as label/value pairs; empty values read "Not set" */
export const DefinitionList = ({
  items,
  columns = 2
}: {
  items: DefinitionItem[]
  columns?: 1 | 2 | 3
}) => (
  <dl
    className={cn(
      'grid gap-x-6',
      columns === 2 && 'sm:grid-cols-2',
      columns === 3 && 'sm:grid-cols-2 lg:grid-cols-3'
    )}
  >
    {items.map((item) => (
      <div
        className="space-y-0.5 border-b border-border/60 py-2.5"
        key={item.label}
      >
        <dt className="text-xs text-muted-foreground">{item.label}</dt>
        <dd
          className={cn(
            'break-words text-sm',
            item.mono && 'font-mono',
            isEmpty(item.value) && 'text-muted-foreground'
          )}
        >
          {isEmpty(item.value) ? 'Not set' : item.value}
        </dd>
      </div>
    ))}
  </dl>
)

type SaveBarProps = {
  changes: string[]
  saving?: boolean
  onSave: () => void
  onDiscard: () => void
}

/**
 * Sticky bar for the Settings and Pricing tier tabs, which save together.
 * Save stays enabled without changes: saving also re-syncs the root role's grants.
 */
export const SaveBar = ({
  changes,
  saving,
  onSave,
  onDiscard
}: SaveBarProps) => (
  <div
    aria-label="Unsaved changes"
    className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-popover px-4 py-3 shadow-lg"
    role="region"
  >
    <p aria-live="polite" className="min-w-0 text-sm">
      {changes.length === 0 ? (
        <span className="text-muted-foreground">No unsaved changes</span>
      ) : (
        <>
          <span className="font-semibold">
            {changes.length === 1
              ? '1 unsaved change'
              : `${changes.length} unsaved changes`}
          </span>
          <span className="text-muted-foreground"> · {changes.join(', ')}</span>
        </>
      )}
    </p>
    <div className="flex gap-2">
      <Button
        disabled={changes.length === 0 || saving}
        onClick={onDiscard}
        variant="outline"
      >
        Discard
      </Button>
      <Button loading={saving} onClick={onSave}>
        Save changes
      </Button>
    </div>
  </div>
)
